/**
 * @file 请求性能监控模块
 * @description 基于 Performance API 采集请求各阶段耗时，用于超时根因分析和慢请求检测
 */

/**
 * 从 Performance API 中查找匹配的 resource entry
 * @param {string} url - 请求 URL
 * @returns {PerformanceResourceTiming|null}
 */
function findPerformanceEntry(url) {
    try {
        const entries = performance.getEntriesByType('resource');
        return entries.find(e => e.name === url || e.name.includes(url)) || null;
    } catch (e) {
        return null;
    }
}

/**
 * 从 PerformanceResourceTiming 中提取各阶段耗时
 * @param {PerformanceResourceTiming} entry
 * @returns {Object}
 */
function extractTimings(entry) {
    const queueTime = entry.fetchStart - entry.startTime;
    const stalledTime = entry.connectStart > 0 ? entry.connectStart - entry.startTime : -1;
    const dns = entry.domainLookupEnd - entry.domainLookupStart;
    const tcp = entry.connectEnd - entry.connectStart;
    const ssl = entry.secureConnectionStart > 0
        ? entry.connectEnd - entry.secureConnectionStart
        : 0;
    const requestSent = entry.requestStart > 0;
    const responseReceived = entry.responseStart > 0;
    const serverWait = responseReceived ? entry.responseStart - entry.requestStart : -1;
    const contentDownload = responseReceived ? entry.responseEnd - entry.responseStart : -1;
    const totalDuration = entry.responseEnd > 0 ? entry.responseEnd - entry.startTime : -1;

    return {
        queueTime, stalledTime, dns, tcp, ssl,
        requestSent, responseReceived,
        serverWait, contentDownload, totalDuration
    };
}

/**
 * 根据各阶段耗时判定超时根因
 * @param {Object} timings - extractTimings 返回值
 * @param {number} timeout - 超时阈值
 * @returns {string} timeoutType
 *
 * 取值:
 *   QUEUE_TIMEOUT          - 浏览器排队等待连接导致超时（排队时间 ≥ 超时阈值的 50%）
 *   DNS_TIMEOUT            - DNS 解析超时
 *   TCP_TIMEOUT            - TCP 连接超时
 *   SSL_TIMEOUT            - SSL 握手超时
 *   SERVER_RESPONSE_TIMEOUT - 请求已发出，等待服务端响应超时
 *   DOWNLOAD_TIMEOUT       - 响应体下载超时
 *   ACCUMULATED_TIMEOUT    - 各阶段累积导致超时
 */
function classifyTimeoutType(timings, timeout) {
    const { stalledTime, dns, tcp, ssl, requestSent, responseReceived, serverWait, contentDownload } = timings;

    if (stalledTime > 0 && stalledTime >= timeout * 0.5) return 'QUEUE_TIMEOUT';
    if (dns > timeout) return 'DNS_TIMEOUT';
    if (tcp > timeout) return 'TCP_TIMEOUT';
    if (ssl > timeout) return 'SSL_TIMEOUT';
    if (requestSent && !responseReceived) return 'SERVER_RESPONSE_TIMEOUT';
    if (serverWait > timeout) return 'SERVER_RESPONSE_TIMEOUT';
    if (contentDownload > timeout) return 'DOWNLOAD_TIMEOUT';
    return 'ACCUMULATED_TIMEOUT';
}

/**
 * 格式化各阶段耗时为可读字符串
 * @param {Object} timings - extractTimings 返回值
 * @param {number} totalElapsed - 总耗时
 * @returns {Object}
 */
function formatTimingDetails(timings, totalElapsed) {
    const { queueTime, stalledTime, dns, tcp, ssl, serverWait, contentDownload, totalDuration } = timings;
    return {
        queueTime: Math.round(queueTime) + 'ms',
        stalledTime: stalledTime === -1 ? 'N/A' : Math.round(stalledTime) + 'ms',
        dns: Math.round(dns) + 'ms',
        tcp: Math.round(tcp) + 'ms',
        ssl: ssl === 0 ? '0ms(reused)' : Math.round(ssl) + 'ms',
        serverWait: serverWait === -1
            ? '未收到服务端响应'
            : Math.round(serverWait) + 'ms',
        contentDownload: contentDownload === -1
            ? '未收到服务端响应'
            : Math.round(contentDownload) + 'ms',
        totalDuration: totalDuration === -1 ? 'N/A' : Math.round(totalDuration) + 'ms',
        totalElapsed: totalElapsed === -1 ? 'N/A' : totalElapsed + 'ms'
    };
}

// ========== 对外暴露的两个主方法 ==========

/**
 * 分析超时请求的各阶段耗时，判定超时根因
 * 在 errorState 中 ECONNABORTED 时调用
 *
 * @param {Object} response - axios 响应或错误对象
 * @param {number} callTime - 请求发起时的时间戳（executeRequest 中记录）
 * @returns {Object|null} { timeoutType, details, timeout }
 */
export function getTimeoutDetail(response, callTime) {
    const config = response && response.config;
    const url = config && config.url;
    if (!url) return null;

    const timeout = (config && config.timeout) || 30000;
    const totalElapsed = callTime ? Date.now() - callTime : -1;

    const entry = findPerformanceEntry(url);

    if (!entry) {
        return {
            timeoutType: 'REQUEST_NOT_SENT',
            details: {
                reason: 'No performance entry found',
                totalElapsed: totalElapsed === -1 ? 'N/A' : totalElapsed + 'ms'
            },
            timeout
        };
    }

    const timings = extractTimings(entry);
    const timeoutType = classifyTimeoutType(timings, timeout);
    const details = formatTimingDetails(timings, totalElapsed);

    return { timeoutType, details, timeout };
}

/**
 * 采集成功请求的性能数据（用于慢请求检测）
 * 在 executeRequest 中请求成功后调用，挂载到 response.data._requestMetrics
 *
 * @param {Object} config - axios config
 * @param {number} callTime - 请求发起时的时间戳
 * @returns {Object|null}
 */
export function collectRequestMetrics(config, callTime) {
    if (!config || !config.url) return null;

    try {
        const entry = findPerformanceEntry(config.url);
        if (!entry) return null;

        const timings = extractTimings(entry);
        const totalElapsed = callTime ? Date.now() - callTime : -1;

        return {
            url: config.url,
            method: config.method,
            stalledTime: Math.round(timings.stalledTime),
            dns: Math.round(timings.dns),
            tcp: Math.round(timings.tcp),
            ssl: Math.round(timings.ssl),
            serverWait: Math.round(timings.serverWait),
            contentDownload: Math.round(timings.contentDownload),
            totalDuration: Math.round(timings.totalDuration),
            totalElapsed,
            isQueueBlocked: timings.stalledTime > 3000,
            isSlow: totalElapsed > 5000
        };
    } catch (e) {
        return null;
    }
}
